import { QUANT_NG_API_MARKET_GROUP_TO_TV_SESSION_ID } from './constants.js';
import { DateTimeFormatter, Instant } from '@js-joda/core';
import type { LocalTime, ZonedDateTime, ZoneId } from '@js-joda/core';
import { notNull } from '@thinkalpha/common/util/notNullOrUndefined.js';
import { PeriodType } from '@thinkalpha/language-services';
import type { BarLength } from '@thinkalpha/language-services';
import type { MarketData, TypicalTradingScheduleEntry } from '@thinkalpha/quant-ws-client/contracts/market.js';
import type { LibrarySessionId } from 'charting_library';
import { staticContainer } from 'src/StaticContainer.js';

const moduleLogger = staticContainer.get('Logger').getSubLogger({ name: 'TradingViewDatafeedServiceUtil' });

/**
 * Normalizes a time string to TradingView's HHMM format.
 * Handles formats like "09:30", "0930", "9:30", "09:30:00", etc.
 */
export function normalizeTimeString(time: string | undefined): string | null {
    if (!time) return null;

    // Remove any colons and whitespace
    const cleaned = time.replace(/[:\s]/g, '');

    // Validate it looks like a time (3-6 digits: HMM, HHMM, HMMSS, or HHMMSS)
    if (!/^\d{3,6}$/.test(cleaned)) {
        return null;
    }

    // If seconds are present (5-6 digits), strip them first
    const withoutSeconds = cleaned.length > 4 ? cleaned.slice(0, -2) : cleaned;

    // Pad to 4 digits if needed (e.g., "930" -> "0930")
    return withoutSeconds.padStart(4, '0');
}

/**
 * Builds a TradingView session string from market groups.
 * Format: "HHMM-HHMM" or "HHMM-HHMM,HHMM-HHMM" for multiple sessions.
 */
export function buildSessionString(marketGroups: { name: string; start: string; end: string }[]): string {
    const sessions = marketGroups
        .map((group) => {
            const start = normalizeTimeString(group.start);
            const end = normalizeTimeString(group.end);
            if (start && end) {
                return `${start}-${end}`;
            }
            return null;
        })
        .filter(notNull);

    return sessions.join(',');
}

/**
 * Formats a LocalTime to TradingView's HHMM format.
 */
export function formatLocalTime(localTime: LocalTime): string {
    const hour = localTime.hour().toString().padStart(2, '0');
    const minute = localTime.minute().toString().padStart(2, '0');
    return `${hour}${minute}`;
}

/**
 * Aligns a session string to a bar boundary for clock-aligned bars.
 * TradingView aligns intraday bars to the session start time. By setting the session
 * start to a bar boundary, we get clock-aligned bars instead of session-aligned bars.
 *
 * Multi-segment sessions are consolidated into a single range (earliest start to latest end)
 * to avoid segment boundary issues with TradingView.
 *
 * @param sessionString The session string, e.g. "0930-1600" or "0415-0930,0930-1600,1600-2000"
 * @param barLengthMinutes The bar length in minutes to align to (e.g., 60 for hourly, 30 for 30-min bars)
 * @returns The aligned session string
 *
 * Examples with barLengthMinutes=60:
 *   "0930-1600" -> "0900-1600"
 *   "0415-0930,0930-1600,1600-2000" -> "0400-2000"
 *
 * Examples with barLengthMinutes=15:
 *   "0922-1600" -> "0915-1600"
 */
export function alignSessionToBarBoundary(sessionString: string, barLengthMinutes: number): string {
    // Split into segments (comma-separated)
    const segments = sessionString.split(',');

    // Parse all segments to find earliest start and latest end
    let earliestStart: number | null = null;
    let latestEnd: string | null = null;

    for (const segment of segments) {
        // Match start-end pattern
        const match = segment.match(/^(\d{2})(\d{2})-(\d{4})$/);
        if (!match) {
            // Can't parse, return original string
            return sessionString;
        }

        const [, hourStr, minuteStr, endTime] = match;
        const totalMinutes = parseInt(hourStr, 10) * 60 + parseInt(minuteStr, 10);

        if (earliestStart === null || totalMinutes < earliestStart) {
            earliestStart = totalMinutes;
        }
        // Take the last segment's end time (they should be contiguous)
        latestEnd = endTime;
    }

    if (earliestStart === null || latestEnd === null) {
        return sessionString;
    }

    // Align the start to bar boundary
    const alignedTotalMinutes = Math.floor(earliestStart / barLengthMinutes) * barLengthMinutes;
    const alignedHour = Math.floor(alignedTotalMinutes / 60);
    const alignedMinute = alignedTotalMinutes % 60;

    const alignedStart = `${alignedHour.toString().padStart(2, '0')}${alignedMinute.toString().padStart(2, '0')}`;

    return `${alignedStart}-${latestEnd}`;
}

/**
 * Builds a TradingView session string from typical schedule entries.
 * Format: "HHMM-HHMM" or "HHMM-HHMM,HHMM-HHMM" for multiple sessions.
 */
export function buildSessionStringFromTypicalSchedule(scheduleEntries: TypicalTradingScheduleEntry[]): string {
    const sessions = scheduleEntries
        .filter((entry) => entry.status !== 'Closed')
        .map((entry) => {
            const start = formatLocalTime(entry.startTime);
            const end = formatLocalTime(entry.endTime);
            return `${start}-${end}`;
        })
        .filter(notNull);

    return sessions.join(',');
}

/**
 * Maps a typical schedule entry to a TradingView session ID.
 * Returns session IDs like 'premarket', 'regular', or 'postmarket' for styling.
 */
export function mapScheduleEntryToSessionId(entry: TypicalTradingScheduleEntry): LibrarySessionId {
    // Try name-based mapping first
    if (entry.name) {
        const mapped = QUANT_NG_API_MARKET_GROUP_TO_TV_SESSION_ID[entry.name];
        if (mapped) return mapped;
    }

    // Fall back to phase-based mapping
    if (entry.phase.includes('Primary Trading')) return 'regular';
    if (entry.phase.includes('Pre-Trading') || entry.phase.includes('Pre-Open')) return 'premarket';
    if (entry.phase.includes('Post-Trading')) return 'postmarket';

    // Default based on status - prefer 'regular' for Open, 'premarket' for Extended
    moduleLogger.warn(
        `Unable to map schedule entry to session ID, defaulting based on status: ${JSON.stringify(entry)}`,
        { entry },
    );
    return entry.status === 'Extended' ? 'premarket' : 'regular';
}

/**
 * Determines if a session string represents a market that spans midnight.
 * Session strings are in format "HHMM-HHMM" or "HHMM-HHMM,HHMM-HHMM" for multiple sessions.
 * If end time < start time for any session segment, the market spans midnight.
 */
export function sessionSpansMidnight(sessionString: string | undefined): boolean {
    if (!sessionString) return false;

    // Check each session segment (comma-separated)
    const segments = sessionString.split(',');
    for (const segment of segments) {
        const match = segment.match(/^(\d{4})-(\d{4})$/);
        if (match) {
            const startTime = parseInt(match[1], 10);
            const endTime = parseInt(match[2], 10);
            // If end time is less than start time, session spans midnight
            if (endTime < startTime) {
                return true;
            }
        }
    }
    return false;
}

/**
 * Converts a server bar timestamp to TradingView's expected bar start time.
 *
 * The server marks the _end_ of bars, but TradingView expects the _beginning_ timestamp.
 * This function first clamps the timestamp to the previous bar boundary, then subtracts
 * one bar length to convert from end-of-bar to start-of-bar semantics.
 *
 * For interday bars (day, week, month), the server timestamp is at the end of the trading day.
 * If the market spans midnight (e.g., trading ends at 2 AM the next calendar day), we need to
 * subtract a day to get the correct trading day.
 *
 * @param timestamp - The server timestamp in milliseconds
 * @param barLength - The bar length specification
 * @param timezone - The timezone to use for calculations
 * @param sessionString - Optional session string (e.g., "0930-1600") to detect midnight-spanning markets
 */
export function getBarStartTime(
    timestamp: number,
    barLength: BarLength,
    timezone: ZoneId,
    sessionString?: string,
): number {
    const instant = Instant.ofEpochMilli(timestamp);
    const zdt = instant.atZone(timezone);
    let truncated: ZonedDateTime;

    // For interday bars, check if market spans midnight
    const spansMidnight = sessionSpansMidnight(sessionString);

    switch (barLength.period) {
        case PeriodType.second: {
            const truncatedSecond = Math.floor(zdt.second() / barLength.value) * barLength.value;
            truncated = zdt.withSecond(truncatedSecond).withNano(0);
            // Subtract one bar length to convert from end-of-bar to start-of-bar
            truncated = truncated.minusSeconds(barLength.value);
            break;
        }
        case PeriodType.minute: {
            const truncatedMinute = Math.floor(zdt.minute() / barLength.value) * barLength.value;
            truncated = zdt.withMinute(truncatedMinute).withSecond(0).withNano(0);
            // Subtract one bar length to convert from end-of-bar to start-of-bar
            truncated = truncated.minusMinutes(barLength.value);
            break;
        }
        case PeriodType.hour: {
            const truncatedHour = Math.floor(zdt.hour() / barLength.value) * barLength.value;
            truncated = zdt.withHour(truncatedHour).withMinute(0).withSecond(0).withNano(0);
            // Subtract one bar length to convert from end-of-bar to start-of-bar
            truncated = truncated.minusHours(barLength.value);
            break;
        }
        case PeriodType.day:
            // For interday bars, the server marks the end-of-trading-day, but TradingView
            // expects 00:00:00 of that trading day. We truncate to midnight.
            truncated = zdt.withHour(0).withMinute(0).withSecond(0).withNano(0);
            // If market spans midnight, the server timestamp is on the next calendar day,
            // so we need to subtract a day to get the correct trading day
            if (spansMidnight) {
                truncated = truncated.minusDays(1);
            }
            break;
        case PeriodType.week: {
            // If market spans midnight, the server timestamp is on the next calendar day,
            // so we need to subtract a day FIRST to get the correct trading day
            const adjustedZdt = spansMidnight ? zdt.minusDays(1) : zdt;
            // js-joda uses ISO week (1=Monday to 7=Sunday), original code used Sunday as week start
            const dayOfWeek = adjustedZdt.dayOfWeek().value();
            const daysToSubtract = dayOfWeek % 7; // Sunday (7) -> 0, Monday (1) -> 1, etc.
            truncated = adjustedZdt.minusDays(daysToSubtract).withHour(0).withMinute(0).withSecond(0).withNano(0);
            break;
        }
        case PeriodType.month:
            // If market spans midnight, the server timestamp is on the next calendar day,
            // so we need to subtract a day FIRST to get the correct month
            if (spansMidnight) {
                truncated = zdt.minusDays(1).withDayOfMonth(1).withHour(0).withMinute(0).withSecond(0).withNano(0);
            } else {
                truncated = zdt.withDayOfMonth(1).withHour(0).withMinute(0).withSecond(0).withNano(0);
            }
            break;
        default:
            throw new Error(`Unsupported period type: ${barLength.period}`);
    }

    return truncated.toInstant().toEpochMilli();
}

const yyyyMMddPattern = DateTimeFormatter.ofPattern('yyyyMMdd');
const sessionHolidaysLogger = moduleLogger.getSubLogger({ name: 'SessionHolidays' });
export function buildSessionHolidays(marketData: MarketData): string | undefined {
    try {
        const holidayDates = marketData.dayExceptions
            .filter((day) => !day.isOpen)
            .map((day) => day.date.format(yyyyMMddPattern))
            .join(',');

        if (TRACE) {
            sessionHolidaysLogger.trace('Fetched holidays from market master', {
                micCode: marketData.micCode,
                holidayCount: holidayDates ? holidayDates.split(',').length : 0,
                holidayDates,
            });
        }

        return holidayDates || undefined;
    } catch (error) {
        sessionHolidaysLogger.warn('Failed to extract holidays', { error });
        return undefined;
    }
}
